import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiKeysRagApi, ApiKeyCreate } from "../services/api-keys-rag-service";
import { toast } from "react-toastify";
import i18n from "../i18n";

export const API_KEYS_QUERY_KEY = ["apiKeys"];

function extractErrorMessage(error: any, fallbackKey: string): string {
  const data = error.response?.data;
  const message =
    data?.error?.message ||
    data?.detail ||
    data?.message;
  return message || i18n.t(fallbackKey);
}

interface UseApiKeysParams {
  page: number;
  pageSize: number;
  projectIds?: string[];
}

export const useApiKeys = ({ page, pageSize, projectIds }: UseApiKeysParams) => {
  return useQuery({
    queryKey: [...API_KEYS_QUERY_KEY, page, pageSize, projectIds ?? []],
    queryFn: () => apiKeysRagApi.listApiKeys(page, pageSize, projectIds),
    placeholderData: (previousData) => previousData,
  });
};

export const useCreateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ApiKeyCreate) => apiKeysRagApi.createApiKey(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, "apiKeys.failedToCreate"));
    },
  });
};

export const useDeleteApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiKeysRagApi.deleteApiKey(id),
    onSuccess: () => {
      toast.success(i18n.t("apiKeys.deletedSuccessfully"));
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, "apiKeys.failedToDelete"));
    },
  });
};

export const useRotateApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiKeysRagApi.rotateApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY });
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, "apiKeys.failedToRotate"));
    },
  });
};
